import { z } from 'zod';

// Operations (PRD §19–20): data retention policy, retention dry run, contact data export and erasure.
// Browser-safe. The retention policy lives in Workspace.settings.retention (validated by RetentionPolicySchema).

const Id = z.string().min(1).max(64);
const Iso = z.string();

// ───────────── Retention policy ─────────────

/** Minimum audit-log retention (days). The audit log is never pruned below 12 months. */
export const AUDIT_LOG_MIN_DAYS = 365;

/** Pilot defaults (PRD §19): raw webhook payloads 30 days, recordings 90 days, operational transcripts 12 months. */
export const RETENTION_DEFAULTS = {
  rawPayloadDays: 30,
  messageBodyDays: 365,
  transcriptDays: 365,
  recordingDays: 90,
  inboundMediaDays: 365,
  supersededDocumentDays: 180,
  webhookDeliveryDays: 30,
  auditLogDays: null,
  legalHold: false,
} as const;

const days = (min: number, max = 3650) => z.number().int().min(min, `Mínimo ${min} días`).max(max, `Máximo ${max} días`);

/**
 * Stored policy. `null` for supersededDocumentDays/auditLogDays = keep indefinitely.
 * legalHold = true stops every retention deletion/redaction for the workspace (the job skips it entirely).
 */
export const RetentionPolicySchema = z.object({
  rawPayloadDays: days(7).default(RETENTION_DEFAULTS.rawPayloadDays),
  messageBodyDays: days(30).default(RETENTION_DEFAULTS.messageBodyDays),
  transcriptDays: days(30).default(RETENTION_DEFAULTS.transcriptDays),
  recordingDays: days(7).default(RETENTION_DEFAULTS.recordingDays),
  inboundMediaDays: days(30).default(RETENTION_DEFAULTS.inboundMediaDays),
  supersededDocumentDays: days(30).nullable().default(RETENTION_DEFAULTS.supersededDocumentDays),
  webhookDeliveryDays: days(7).default(RETENTION_DEFAULTS.webhookDeliveryDays),
  auditLogDays: days(AUDIT_LOG_MIN_DAYS, 3650 * 3).nullable().default(RETENTION_DEFAULTS.auditLogDays),
  legalHold: z.boolean().default(false),
  legalHoldReason: z.string().trim().max(300).nullable().default(null),
});
export type RetentionPolicy = z.infer<typeof RetentionPolicySchema>;

export const UpdateRetentionPolicySchema = z.object({
  rawPayloadDays: days(7),
  messageBodyDays: days(30),
  transcriptDays: days(30),
  recordingDays: days(7),
  inboundMediaDays: days(30),
  supersededDocumentDays: days(30).nullable(),
  webhookDeliveryDays: days(7),
  auditLogDays: days(AUDIT_LOG_MIN_DAYS, 3650 * 3).nullable(),
  legalHold: z.boolean(),
  legalHoldReason: z.string().trim().max(300).nullable().optional(),
});
export type UpdateRetentionPolicy = z.infer<typeof UpdateRetentionPolicySchema>;

export const RETENTION_CATEGORIES = [
  'raw_payloads',
  'message_bodies',
  'transcripts',
  'recordings',
  'inbound_media',
  'superseded_documents',
  'webhook_deliveries',
  'outbox_published',
  'audit_log',
] as const;
export const RetentionCategorySchema = z.enum(RETENTION_CATEGORIES);
export type RetentionCategory = z.infer<typeof RetentionCategorySchema>;

export const RETENTION_CATEGORY_LABELS: Record<RetentionCategory, string> = {
  raw_payloads: 'Cargas crudas de proveedores y webhooks de entrada',
  message_bodies: 'Texto de mensajes',
  transcripts: 'Transcripciones y resúmenes de llamadas',
  recordings: 'Grabaciones de llamadas (archivos guardados en LIA y referencias)',
  inbound_media: 'Archivos recibidos (WhatsApp)',
  superseded_documents: 'Archivos de versiones reemplazadas de documentos',
  webhook_deliveries: 'Registro de entregas de webhooks salientes',
  outbox_published: 'Eventos internos ya publicados (cola de salida)',
  audit_log: 'Bitácora de auditoría',
};

export const RetentionCategoryReportSchema = z.object({
  category: RetentionCategorySchema,
  label: z.string(),
  /** null = kept indefinitely (nothing is deleted). */
  retentionDays: z.number().int().nullable(),
  /** Hours for categories with sub-day retention (outbox); otherwise null. */
  retentionHours: z.number().int().nullable(),
  configurable: z.boolean(),
  /** Rows older than this are affected. null when the category keeps data indefinitely. */
  cutoff: Iso.nullable(),
  /** Rows the next run would affect (exact count at the time of the request). */
  count: z.number().int(),
  /** What happens to an affected row (Spanish, shown to admins). */
  action: z.string(),
  /** Provider-side retention that this platform does not control (disclosed, never implied). */
  upstream: z.string().nullable(),
});
export type RetentionCategoryReport = z.infer<typeof RetentionCategoryReportSchema>;

export const RetentionDryRunSchema = z.object({
  generatedAt: Iso,
  legalHold: z.boolean(),
  /** When legalHold is true every count is still shown but the job will not touch anything. */
  categories: z.array(RetentionCategoryReportSchema),
  /** Records kept on purpose, with the reason. */
  kept: z.array(z.object({ label: z.string(), detail: z.string() })),
});
export type RetentionDryRun = z.infer<typeof RetentionDryRunSchema>;

export const RetentionSettingsSchema = z.object({
  policy: RetentionPolicySchema,
  defaults: RetentionPolicySchema,
  auditLogMinDays: z.number().int(),
  lastRun: z
    .object({ at: Iso, counts: z.record(z.string(), z.number().int()), skippedLegalHold: z.boolean() })
    .nullable(),
  upstreamDisclosures: z.array(z.object({ provider: z.string(), detail: z.string() })),
});
export type RetentionSettings = z.infer<typeof RetentionSettingsSchema>;

// ───────────── Contact export / erasure (data subject workflows) ─────────────

export const CONTACT_ERASURE_CONFIRM_TEXT = 'ELIMINAR';

export const ContactDataFollowUpSchema = z.object({
  provider: z.string(),
  /** What the admin must do in the provider (we never call providers during erasure). */
  action: z.string(),
  /** Provider-side references (ids/URIs) to locate the data upstream. */
  references: z.array(z.string()),
});
export type ContactDataFollowUp = z.infer<typeof ContactDataFollowUpSchema>;

export const ContactErasurePreviewSchema = z.object({
  contactId: Id,
  /** Single-use confirmation token, valid for a few minutes and bound to this contact and user. */
  confirmationToken: z.string(),
  expiresAt: Iso,
  confirmText: z.literal(CONTACT_ERASURE_CONFIRM_TEXT),
  isTest: z.boolean(),
  optedOut: z.boolean(),
  counts: z.object({
    identities: z.number().int(),
    interests: z.number().int(),
    conversations: z.number().int(),
    messages: z.number().int(),
    mediaFiles: z.number().int(),
    calls: z.number().int(),
    appointments: z.number().int(),
    pendingOutreach: z.number().int(),
    externalReferences: z.number().int(),
  }),
  /** What is removed, what is kept (anonymized) and why. */
  removes: z.array(z.string()),
  keeps: z.array(z.string()),
  followUps: z.array(ContactDataFollowUpSchema),
});
export type ContactErasurePreview = z.infer<typeof ContactErasurePreviewSchema>;

export const ContactErasureRequestSchema = z.object({
  confirmationToken: z.string().min(20).max(200),
  confirmText: z.literal(CONTACT_ERASURE_CONFIRM_TEXT, { message: `Escribe ${CONTACT_ERASURE_CONFIRM_TEXT} para confirmar` }),
});
export type ContactErasureRequest = z.infer<typeof ContactErasureRequestSchema>;

export const ContactErasureResultSchema = z.object({
  contactId: Id,
  erasedAt: Iso,
  suppressionKept: z.boolean(),
  counts: z.object({
    identitiesDeleted: z.number().int(),
    messagesRedacted: z.number().int(),
    mediaFilesDeleted: z.number().int(),
    /** Call recordings stored by LIA (plan B4) deleted from private storage. */
    recordingFilesDeleted: z.number().int().default(0),
    callsRedacted: z.number().int(),
    rawEventsRedacted: z.number().int(),
    webhookDeliveriesRedacted: z.number().int(),
    outreachCancelled: z.number().int(),
    appointmentsAnonymized: z.number().int(),
  }),
  followUps: z.array(ContactDataFollowUpSchema),
});
export type ContactErasureResult = z.infer<typeof ContactErasureResultSchema>;

/** Streamed JSON download (documented shape; top-level keys in this order). */
export const ContactExportSchema = z.object({
  format: z.literal('lia.contact-export.v1'),
  exportedAt: Iso,
  workspaceId: Id,
  contact: z.record(z.string(), z.unknown()),
  identities: z.array(z.unknown()),
  externalReferences: z.array(z.unknown()),
  interests: z.array(z.unknown()),
  permissions: z.array(z.unknown()),
  suppressions: z.array(z.unknown()),
  conversations: z.array(z.unknown()),
  messages: z.array(z.unknown()),
  calls: z.array(z.unknown()),
  appointments: z.array(z.unknown()),
  bookingIntents: z.array(z.unknown()),
  handoffs: z.array(z.unknown()),
  enrollments: z.array(z.unknown()),
  actionIntents: z.array(z.unknown()),
  campaignRecipients: z.array(z.unknown()),
  audits: z.array(z.unknown()),
  upstream: z.array(ContactDataFollowUpSchema),
});
